import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SurveyTemplate from "../SurveyTemplate";
import "../../styles/components/MultiPageSurvey.css";

const EnergyQuizPage1 = ({
  quizData,
  updateQuizData,
  onNext,
  onNavigationBack,
  currentSection,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [localData, setLocalData] = useState({
    morningLight: quizData.morningLight || "",
    sleepConsistency: quizData.sleepConsistency || "",
    afternoonEnergyDip: quizData.afternoonEnergyDip || "",
    caffeineTiming: quizData.caffeineTiming || "",
    eveningLight: quizData.eveningLight || "",
    nsdrPractice: quizData.nsdrPractice || "",
  });

  const questions = [
    {
      id: "morningLight",
      question: "Do you get sunlight within 30-60 minutes of waking?",
      intro: "Let's optimize your circadian rhythm — the research shows this is protocol #1.",
      options: [
        { value: "daily", label: "☀️ Yes, almost daily" },
        { value: "sometimes", label: "🌤️ Sometimes, when I remember" },
        { value: "rarely", label: "🏠 Rarely — I'm usually indoors" },
        { value: "never", label: "❌ Never thought about it" },
      ],
      info: "Dr. Andrew Huberman's #1 protocol: Morning sunlight sets your circadian clock, improves sleep quality, and boosts mood. 10 min on bright days, 20-30 min if cloudy.",
    },
    {
      id: "sleepConsistency",
      question: "How consistent is your wake-up time?",
      intro: "Here's what the research shows — same wake time > same bedtime.",
      options: [
        { value: "very", label: "🕕 Same time ±15 min daily" },
        { value: "somewhat", label: "🕕 Within an hour most days" },
        { value: "variable", label: "🕖 Varies by 1-2 hours" },
        { value: "random", label: "🔀 Completely random" },
      ],
      info: "Huberman emphasizes that consistent wake times anchor your circadian rhythm more powerfully than consistent bedtimes. Your body craves this predictability.",
    },
    {
      id: "afternoonEnergyDip",
      question: "How's your energy between 1-4 PM?",
      intro: "Let's decode your natural cortisol and adenosine patterns.",
      options: [
        { value: "stable", label: "💪 Stable energy" },
        { value: "mild_dip", label: "📉 Mild dip, manageable" },
        { value: "crash", label: "💤 Major crash, need caffeine/nap" },
        { value: "sleepy", label: "😴 Fight to stay awake" },
      ],
      info: "Afternoon energy dips reflect your body's natural circadian rhythm and adenosine buildup. Severe crashes may signal cortisol dysregulation or sleep debt.",
    },
    {
      id: "caffeineTiming",
      question: "When do you have your first caffeine?",
      intro: "Timing matters more than you think — here's the Huberman protocol.",
      options: [
        { value: "delayed", label: "⏰ 90-120 min after waking" },
        { value: "hour", label: "☕ Within 1 hour of waking" },
        { value: "immediately", label: "☕ Immediately upon waking" },
        { value: "none", label: "🚫 I don't drink caffeine" },
      ],
      info: "Huberman's protocol: Delay caffeine 90-120 min after waking to avoid afternoon crashes. This allows natural cortisol awakening response and prevents adenosine receptor blocking.",
    },
    {
      id: "eveningLight",
      question: "How's your light exposure 2-3 hours before bed?",
      intro: "Blue light disrupts melatonin — but the research shows it's about timing and intensity.",
      options: [
        { value: "dim", label: "🕯️ Very dim, warm lighting only" },
        { value: "moderate", label: "💡 Some bright lights, trying to dim" },
        { value: "bright", label: "💻 Bright screens, normal lighting" },
        { value: "very_bright", label: "📱 Bright screens until bedtime" },
      ],
      info: "Huberman's research: Bright light after 8 PM disrupts dim light melatonin onset (DLMO). Use dim, warm lights to support natural melatonin production.",
    },
    {
      id: "nsdrPractice",
      question: "Do you practice NSDR (Non-Sleep Deep Rest) or yoga nidra?",
      intro: "Huberman's signature protocol for recovery and brain restoration.",
      options: [
        { value: "regular", label: "🧘 Yes, regularly" },
        { value: "occasional", label: "🤔 Occasionally" },
        { value: "tried", label: "💭 I've tried it" },
        { value: "never", label: "❓ What's NSDR?" },
      ],
      info: "NSDR (yoga nidra, meditation, light naps) helps restore dopamine, reduce cortisol, and improve neuroplasticity. Huberman calls it 'the ultimate recovery tool.'",
    },
  ];

  const handleOptionSelect = (value) => {
    const currentQ = questions[currentQuestion];
    setLocalData((prev) => ({ ...prev, [currentQ.id]: value }));
    setTimeout(() => handleNext(value), 300);
  };

  const handleNext = (overrideValue) => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      Object.keys(localData).forEach((key) => {
        const val = overrideValue !== undefined && key === questions[currentQuestion].id ? overrideValue : localData[key];
        updateQuizData(key, val);
      });
      onNext();
    }
  };

  const handleNavigationBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (onNavigationBack) {
      onNavigationBack();
    }
  };

  const currentQ = questions[currentQuestion];
  const isAnswered = localData[currentQ.id] !== "";
  const totalQuestions = 18; // total across all 3 pages (6 each)
  const globalIndex = currentQuestion; // page 1 starts at 0

  return (
    <div className="survey-page-1">
      <SurveyTemplate
        introText={currentQ.intro}
        questionText={currentQ.question}
        options={currentQ.options || []}
        infoSections={[{ title: "Why this matters", content: currentQ.info }]}
        onOptionSelect={handleOptionSelect}
        selectedOption={localData[currentQ.id]}
        onBack={() => onNavigationBack && onNavigationBack()}
        progressContainer={
          <div className="progress-container">
            <div className="question-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${((globalIndex + 1) / totalQuestions) * 100}%` }} />
              </div>
              <span className="progress-text">{globalIndex + 1} of {totalQuestions}</span>
            </div>
          </div>
        }
        navigationButtons={
          <div className="navigation-buttons">
            <button className="nav-button back-button" onClick={handleNavigationBack} disabled={currentQuestion === 0}>
              {t("common.back", { defaultValue: "Back" })}
            </button>
            <button className="nav-button next-button" onClick={() => handleNext()} disabled={!isAnswered}>
              {t("common.next", { defaultValue: "Next" })}
            </button>
          </div>
        }
      />
    </div>
  );
};

export default EnergyQuizPage1;
